import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Globe, Play, BarChart2, CheckCircle, XCircle,
  RefreshCw, Search, ChevronDown, ChevronRight, ChevronLeft,
  AlertTriangle, Copy, Smartphone, Layers, Link, Download, Filter
} from 'lucide-react';


// ─── Input detection helpers ──────────────────────────────────────────────────



// ─── Sellers.json badge ───────────────────────────────────────────────────────
function SellersJsonBadge({ sj }) {
  const [open, setOpen] = useState(false);
  if (!sj) return <span className="badge badge-muted">No SSP parsed</span>;
  if (!sj.fetched) return <span className="badge badge-amber" title={sj.error}><AlertTriangle size={10} /> sellers.json unreachable</span>;
  if (!sj.found) return <span className="badge badge-red"><XCircle size={10} /> ID not in sellers.json</span>;
  const s = sj.seller;
  return (
    <div style={{ display: 'inline-block' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        background: 'var(--green-dim)', color: 'var(--green)',
        border: '1px solid color-mix(in srgb, var(--green) 20%, transparent)', borderRadius: '3px',
        padding: '2px 8px', fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)'
      }}>
        <CheckCircle size={10} /> Found in sellers.json
        {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
      </button>
      {open && (
        <div style={{ marginTop: '6px', padding: '10px', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '11px', fontFamily: 'var(--font-mono)', lineHeight: 1.8, minWidth: '260px' }}>
          {[['seller_id', s.seller_id], ['name', s.name], ['domain', s.domain], ['seller_type', s.seller_type], ['is_confidential', s.is_confidential !== undefined ? String(s.is_confidential) : undefined]]
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: '8px' }}>
                <span style={{ color: 'var(--text-muted)', minWidth: '120px' }}>{k}:</span>
                <span style={{ color: 'var(--cyan)' }}>{String(v)}</span>
              </div>
            ))}
          <div style={{ color: 'var(--text-muted)', fontSize: '10px', marginTop: '2px' }}>{sj.totalSellersInFile} total sellers in {sj.sspDomain} sellers.json</div>
        </div>
      )}
    </div>
  );
}

// ─── Domain result card ───────────────────────────────────────────────────────
function DomainResultCard({ result, inputMode }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: `1px solid ${result.success ? 'var(--border)' : 'color-mix(in srgb, var(--red) 30%, transparent)'}`,
      borderRadius: 'var(--radius)', marginBottom: '8px', overflow: 'hidden'
    }}>
      {/* Header */}
      <div onClick={() => setExpanded(e => !e)} style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px 14px', cursor: 'pointer',
        background: expanded ? 'var(--bg-elevated)' : 'transparent',
        borderBottom: expanded ? '1px solid var(--border)' : 'none', flexWrap: 'wrap'
      }}>
        {expanded ? <ChevronDown size={13} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={13} style={{ color: 'var(--text-muted)' }} />}
        {result.success ? <CheckCircle size={13} style={{ color: 'var(--green)', minWidth: 13 }} /> : <XCircle size={13} style={{ color: 'var(--red)', minWidth: 13 }} />}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
          {/* Bundle mode: show app name + bundle ID */}
          {inputMode === 'bundles' && result.bundleId ? (
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '13px' }}>
                {result.appName || result.bundleId}
                {result.platform === 'ios' && <span className="badge badge-cyan" style={{ marginLeft: '6px', fontSize: '9px' }}>iOS</span>}
                {result.platform === 'android' && <span className="badge badge-green" style={{ marginLeft: '6px', fontSize: '9px' }}>Android</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                <code style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{result.bundleId}</code>
                {result.domain && <>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>→</span>
                  <code style={{ fontSize: '10px', color: 'var(--cyan)' }}>{result.domain}</code>
                </>}
              </div>
            </div>
          ) : (
            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
              {result.domain || result.bundleId || 'Unknown'}
            </span>
          )}
        </div>

        {result.success && (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <span className="badge badge-muted">{result.totalRecords} records</span>
            <span className="badge badge-cyan">{result.directCount} D</span>
            <span className="badge badge-purple">{result.resellerCount} R</span>
          </div>
        )}
        {!result.success && (
          result.notOnStore
            ? <span className="badge badge-amber" title={result.error}>{result.notOnStoreLabel || 'Not on store'}</span>
            : <span className="badge badge-red">{result.error}</span>
        )}

        {!result.notOnStore && result.lineResults && result.lineResults.map((lr, i) => (
          <span key={i} className={`badge ${lr.found ? 'badge-green' : 'badge-red'}`} style={{ fontSize: '10px' }}>
            {lr.found ? `✓ ${lr.duplicateCount}×` : '✗'} L{i + 1}
          </span>
        ))}
        <span className="badge badge-muted" style={{ marginLeft: 'auto' }}>{result.fetchTimeMs}ms</span>
      </div>

      {/* Expanded line results */}
      {expanded && result.lineResults && result.lineResults.length > 0 && (
        <div style={{ padding: '12px 14px' }}>
          {result.lineResults.map((lr, i) => (
            <div key={i} style={{
              marginBottom: '12px', padding: '10px 12px',
              background: lr.found ? 'color-mix(in srgb, var(--green) 4%, transparent)' : 'color-mix(in srgb, var(--red) 4%, transparent)',
              border: `1px solid ${lr.found ? 'color-mix(in srgb, var(--green) 15%, transparent)' : 'color-mix(in srgb, var(--red) 15%, transparent)'}`,
              borderRadius: 'var(--radius-sm)'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: '50px', marginTop: '2px' }}>Line {i + 1}</span>
                <code style={{ background: 'var(--bg-base)', padding: '2px 8px', borderRadius: '3px', fontSize: '11px', color: 'var(--text-secondary)', border: '1px solid var(--border)', flex: 1, wordBreak: 'break-all' }}>{lr.searchLine}</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {lr.found ? (
                  <>
                    <span className="badge badge-green"><CheckCircle size={10} /> Found</span>
                    {/* A domain search returns N DIFFERENT lines from that exchange.
                        Calling them duplicates, as an exact search correctly does,
                        would be plainly wrong. */}
                    <span className="badge badge-cyan">
                      {lr.matchMode === 'domain'
                        ? (lr.duplicateCount === 1 ? '1 line' : `${lr.duplicateCount} lines`)
                        : (lr.duplicateCount === 1 ? '1 occurrence' : `${lr.duplicateCount} duplicates`)}
                    </span>
                    {lr.matchMode === 'domain' && (
                      <span className="badge badge-muted" style={{ fontSize: '10px' }}>all lines for this exchange</span>
                    )}
                  </>
                ) : (
                  <span className="badge badge-red">
                    <XCircle size={10} />{' '}
                    {lr.matchMode === 'domain'
                      ? `No ${lr.searchDomain} lines in ${result.domain}`
                      : `Not found in ${result.domain}`}
                  </span>
                )}
              </div>
              {/* Domain mode: each matched seller id resolved against the exchange's
                  OWN sellers.json. "Not in their sellers.json" is a real finding;
                  a file we could not READ is a different thing and says so. */}
              {lr.matchMode === 'domain' && lr.found && lr.matchedSellers && lr.matchedSellers.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Checked against {lr.searchDomain}/sellers.json:
                  </div>
                  {lr.matchedSellers.map((ms, si) => (
                    <div key={si} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', background: 'var(--bg-base)', padding: '4px 8px', borderRadius: '3px', marginBottom: '3px', border: '1px solid var(--border)' }}>
                      <code style={{ fontSize: '11px', color: 'var(--text-secondary)', minWidth: 70 }}>{ms.sellerId || '\u2014'}</code>
                      <span className="badge badge-muted" style={{ fontSize: '10px' }}>{ms.accountType || '\u2014'}</span>
                      {!ms.sellersJsonFetched
                        ? <span className="badge badge-muted" style={{ fontSize: '10px' }} title="Their sellers.json could not be read, so there is nothing to compare against">sellers.json unreadable</span>
                        : ms.seller
                          ? <>
                              <span className="badge badge-green" style={{ fontSize: '10px' }}>{ms.seller.seller_type || 'listed'}</span>
                              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                {ms.seller.name || (ms.seller.is_confidential ? 'Confidential' : '\u2014')}
                                {ms.seller.domain ? ` \u00b7 ${ms.seller.domain}` : ''}
                              </span>
                            </>
                          : <span className="badge badge-amber" style={{ fontSize: '10px' }} title="This seller id is declared in the publisher's file but is NOT in the exchange's own sellers.json">not in their sellers.json</span>}
                    </div>
                  ))}
                </div>
              )}

              {lr.found && lr.matchedLines && lr.matchedLines.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {lr.matchMode === 'domain'
                      ? `Lines for ${lr.searchDomain} in this file (${lr.duplicateCount}):`
                      : `As it appears in file${lr.duplicateCount > 1 ? ` (${lr.duplicateCount}\u00d7)` : ''}:`}
                  </div>
                  {lr.matchedLines.map((ml, mi) => (
                    <div key={mi} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--bg-base)', padding: '4px 8px', borderRadius: '3px', marginBottom: '3px', border: '1px solid var(--border)' }}>
                      <code style={{ fontSize: '11px', color: 'var(--green)', flex: 1, wordBreak: 'break-all' }}>{ml}</code>
                      <button onClick={() => { navigator.clipboard.writeText(ml); toast.success('Copied!'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' }}>
                        <Copy size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '3px' }}>sellers.json:</span>
                <SellersJsonBadge sj={lr.sellersJson} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No line results — show basic stats */}
      {expanded && (!result.lineResults || result.lineResults.length === 0) && result.success && (
        <div style={{ padding: '10px 14px', fontSize: '12px', color: 'var(--text-muted)' }}>
          {result.totalRecords} records · {result.directCount} DIRECT · {result.resellerCount} RESELLER · {result.uniqueSsps} unique SSPs
        </div>
      )}
    </div>
  );
}

// ─── Sellers.json expandable cell ────────────────────────────────────────────
function SellersJsonCell({ sj }) {
  const [open, setOpen] = useState(false);
  if (!sj) return <span className="badge badge-muted" style={{ fontSize:'10px' }}>—</span>;
  if (!sj.fetched) return (
    <span className="badge badge-amber" style={{ fontSize:'10px' }} title={sj.error}>
      <AlertTriangle size={9}/> Unreachable
    </span>
  );
  if (!sj.found) return (
    <span className="badge badge-red" style={{ fontSize:'10px' }}>
      <XCircle size={9}/> Not listed
    </span>
  );
  const s = sj.seller;
  return (
    <div>
      <button onClick={() => setOpen(o => !o)} style={{
        display:'inline-flex', alignItems:'center', gap:'4px',
        background:'var(--green-dim)', color:'var(--green)',
        border:'1px solid color-mix(in srgb, var(--green) 25%, transparent)', borderRadius:'3px',
        padding:'2px 7px', fontSize:'10px', cursor:'pointer', fontFamily:'var(--font-mono)'
      }}>
        <CheckCircle size={9}/> {s?.seller_type || 'Listed'}
        {open ? <ChevronDown size={9}/> : <ChevronRight size={9}/>}
      </button>
      {open && (
        <div style={{
          marginTop:'6px',
          padding:'8px 10px', background:'var(--bg-base)',
          border:'1px solid var(--border)', borderRadius:'var(--radius-sm)',
          fontSize:'11px', fontFamily:'var(--font-mono)', lineHeight:1.9,
        }}>
          {[['seller_id', s?.seller_id], ['name', s?.name], ['domain', s?.domain],
            ['seller_type', s?.seller_type],
            ['is_confidential', s?.is_confidential !== undefined ? String(s.is_confidential) : undefined]]
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => (
              <div key={k} style={{ display:'flex', gap:'8px' }}>
                <span style={{ color:'var(--text-muted)', minWidth:'110px', flexShrink:0 }}>{k}:</span>
                <span style={{ color:'var(--cyan)', wordBreak:'break-all' }}>{String(v)}</span>
              </div>
            ))}
          <div style={{ color:'var(--text-muted)', fontSize:'10px', marginTop:'4px', borderTop:'1px solid var(--border)', paddingTop:'4px' }}>
            {sj.totalSellersInFile?.toLocaleString()} total sellers · {sj.sspDomain}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Single expandable bundle result row ─────────────────────────────────────
function BundleResultRow({ result, searchLines }) {
  const [expanded, setExpanded] = useState(false);
  const r = result;
  const hasLines = searchLines && searchLines.length > 0;

  const adsTxtUrl   = r.domain ? `https://${r.domain}/ads.txt`      : null;
  const appAdsTxtUrl = r.domain ? `https://${r.domain}/app-ads.txt` : null;

  return (
    <div style={{
      border:`1px solid ${r.success ? 'var(--border)' : 'color-mix(in srgb, var(--red) 30%, transparent)'}`,
      borderRadius:'var(--radius-sm)', marginBottom:'6px', overflow:'hidden',
      background:'var(--bg-card)'
    }}>
      {/* Collapsed header — always visible */}
      <div onClick={() => setExpanded(e => !e)} style={{
        display:'flex', alignItems:'center', gap:'10px',
        padding:'10px 14px', cursor:'pointer', flexWrap:'wrap',
        background: expanded ? 'var(--bg-elevated)' : 'transparent',
        borderBottom: expanded ? '1px solid var(--border)' : 'none',
      }}>
        {expanded
          ? <ChevronDown size={13} style={{ color:'var(--text-muted)', flexShrink:0 }}/>
          : <ChevronRight size={13} style={{ color:'var(--text-muted)', flexShrink:0 }}/>}

        {r.success
          ? <CheckCircle size={13} style={{ color:'var(--green)', flexShrink:0 }}/>
          : <XCircle    size={13} style={{ color:'var(--red)',   flexShrink:0 }}/>}

        {/* Bundle ID + App name + store link */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            <code style={{ fontSize:'12px', color:'var(--text-primary)', fontFamily:'var(--font-mono)', fontWeight:700 }}>
              {r.bundleId || r.domain}
            </code>
            {r.storeUrl && (
              <a href={r.storeUrl} target="_blank" rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{ color:'var(--text-muted)', flexShrink:0 }}
                title="View on store">
                <Link size={11}/>
              </a>
            )}
          </div>
          {r.appName && (
            <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'1px' }}>{r.appName}</div>
          )}
        </div>

        {/* Platform badge */}
        <div style={{ flexShrink:0 }}>
          {r.platform === 'ios'         && <span className="badge badge-cyan"   style={{ fontSize:'10px' }}>iOS</span>}
          {r.platform === 'android'     && <span className="badge badge-green"  style={{ fontSize:'10px' }}>Android</span>}
          {r.platform === 'roku'        && <span className="badge badge-purple" style={{ fontSize:'10px' }}>Roku</span>}
          {r.platform === 'tvos'        && <span className="badge badge-cyan"   style={{ fontSize:'10px' }}>tvOS</span>}
          {r.platform === 'android_tv'  && <span className="badge badge-green"  style={{ fontSize:'10px' }}>Android TV</span>}
          {r.platform === 'firetv'      && <span className="badge badge-amber"  style={{ fontSize:'10px' }}>Fire TV</span>}
          {r.platform === 'samsung'     && <span className="badge badge-muted"  style={{ fontSize:'10px', color:'#1428A0' }}>Samsung</span>}
          {r.platform === 'androidtv'   && <span className="badge badge-green"  style={{ fontSize:'10px' }}>Android TV</span>}
          {r.platform === 'lg'          && <span className="badge badge-red"    style={{ fontSize:'10px' }}>LG</span>}
          {r.platform === 'vizio'       && <span className="badge badge-amber"  style={{ fontSize:'10px' }}>Vizio</span>}
          {r.platform === 'unknown_ctv' && <span className="badge badge-muted"  style={{ fontSize:'10px' }}>CTV</span>}
        </div>

        {/* Developer domain + ads.txt links */}
        {r.domain
          ? <div style={{ display:'flex', gap:'8px', alignItems:'center', flexShrink:0, flexWrap:'wrap' }}>
              <span style={{ fontSize:'11px', color:'var(--cyan)', fontFamily:'var(--font-mono)' }}>{r.domain}</span>
              <a href={adsTxtUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                style={{ fontSize:'10px', color:'var(--text-muted)', textDecoration:'underline' }}>ads.txt</a>
              <a href={appAdsTxtUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                style={{ fontSize:'10px', color:'var(--text-muted)', textDecoration:'underline' }}>app-ads.txt</a>
            </div>
          : <span style={{ fontSize:'11px', color:'var(--text-muted)', flexShrink:0 }}>Not resolved</span>
        }

        {/* Not-on-store badge — existence check confirmed removed across storefronts */}
        {r.notOnStore && (
          <span className="badge badge-amber" style={{ fontSize:'10px' }} title={r.error}>
            {r.notOnStoreLabel || 'Not on store'}
          </span>
        )}

        {/* Quick line result pills in collapsed view */}
        {hasLines && r.lineResults && !r.notOnStore && (
          <div style={{ display:'flex', gap:'4px', flexShrink:0, flexWrap:'wrap' }}>
            {r.lineResults.map((lr, li) => (
              <span key={li}
                className={`badge ${lr.found ? 'badge-green' : 'badge-red'}`}
                style={{ fontSize:'10px' }}>
                {lr.found ? `✓ L${li+1}` : `✗ L${li+1}`}
              </span>
            ))}
          </div>
        )}

        {/* Error for failed (non-existence failures only) */}
        {!r.success && r.error && !r.notOnStore && (
          <span style={{ fontSize:'10px', color:'var(--red)', maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
            title={r.error}>
            {r.error}
          </span>
        )}
      </div>

      {/* Expanded — line-by-line table */}
      {expanded && r.success && hasLines && r.lineResults?.length > 0 && (
        <div style={{ padding:'12px 14px', overflowX:'auto' }}>
          <table className="data-table" style={{ fontSize:'11px', minWidth:'600px' }}>
            <thead>
              <tr>
                <th style={{ minWidth:'40px' }}>#</th>
                <th style={{ minWidth:'260px' }}>Search Line</th>
                <th style={{ minWidth:'100px' }}>Result</th>
                <th style={{ minWidth:'90px' }}>Occurrences</th>
                <th style={{ minWidth:'200px' }}>As in file</th>
                <th style={{ minWidth:'160px' }}>Sellers.json</th>
              </tr>
            </thead>
            <tbody>
              {r.lineResults.map((lr, li) => (
                <tr key={li} style={{ background: lr.found ? 'color-mix(in srgb, var(--green) 3%, transparent)' : 'color-mix(in srgb, var(--red) 3%, transparent)' }}>
                  <td style={{ color:'var(--text-muted)' }}>{li + 1}</td>
                  <td>
                    <code style={{ fontSize:'10px', color:'var(--text-secondary)', wordBreak:'break-all' }}>
                      {lr.searchLine}
                    </code>
                  </td>
                  <td>
                    {lr.found
                      ? <span className="badge badge-green" style={{ fontSize:'10px' }}><CheckCircle size={9}/> Found</span>
                      : <span className="badge badge-red"   style={{ fontSize:'10px' }}><XCircle    size={9}/> Not found</span>}
                  </td>
                  <td>
                    {lr.found
                      ? <span className="badge badge-cyan" style={{ fontSize:'10px' }}>{lr.duplicateCount}×</span>
                      : <span style={{ color:'var(--text-muted)' }}>—</span>}
                  </td>
                  <td>
                    {lr.found && lr.matchedLines?.length > 0
                      ? <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
                          {lr.matchedLines.map((ml, mi) => (
                            <div key={mi} style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                              <code style={{ fontSize:'10px', color:'var(--green)', wordBreak:'break-all' }}>{ml}</code>
                              <button onClick={() => { navigator.clipboard.writeText(ml); toast.success('Copied!'); }}
                                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:'1px', flexShrink:0 }}>
                                <Copy size={10}/>
                              </button>
                            </div>
                          ))}
                        </div>
                      : <span style={{ color:'var(--text-muted)' }}>—</span>}
                  </td>
                  <td>
                    <SellersJsonCell sj={lr.sellersJson}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expanded — no search lines, show basic stats */}
      {expanded && r.success && !hasLines && (
        <div style={{ padding:'10px 14px', fontSize:'12px', color:'var(--text-muted)', display:'flex', gap:'16px' }}>
          <span>{r.totalRecords} records</span>
          <span style={{ color:'var(--cyan)' }}>{r.directCount} DIRECT</span>
          <span style={{ color:'var(--purple)' }}>{r.resellerCount} RESELLER</span>
          <span>{r.uniqueSsps} unique SSPs</span>
          <span style={{ color:'var(--text-muted)' }}>{r.fetchTimeMs}ms</span>
        </div>
      )}
    </div>
  );
}

// ─── Bundle results list ──────────────────────────────────────────────────────
function BundleResultsTable({ results, searchLines }) {
  return (
    <div>
      {results.map((r, i) => (
        <BundleResultRow key={`${r.bundleId || r.domain}-${i}`} result={r} searchLines={searchLines} />
      ))}
    </div>
  );
}
function ResolutionSummary({ resolvedDomains, inputMode }) {
  const [open, setOpen] = useState(true);
  if (!resolvedDomains || resolvedDomains.length === 0) return null;
  const failed   = resolvedDomains.filter(r => !r.domain);
  const succeeded = resolvedDomains.filter(r => r.domain);
  const isCTV = inputMode === 'ctv';

  return (
    <div className="card" style={{ marginBottom: '16px', borderColor: 'color-mix(in srgb, var(--cyan) 20%, transparent)' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', marginBottom: open ? '12px' : 0 }}>
        {open ? <ChevronDown size={13} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={13} style={{ color: 'var(--text-muted)' }} />}
        <div className="card-title" style={{ margin: 0 }}>
          <Layers size={13} /> {isCTV ? 'CTV Bundle → Developer Domain' : 'Bundle Belongs to Developer'}
        </div>
        <span className="badge badge-green">{succeeded.length} resolved</span>
        {failed.length > 0 && <span className="badge badge-red">{failed.length} not found</span>}
      </div>
      {open && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Bundle ID</th>
                <th>Platform</th>
                <th>Developer (Domain)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {resolvedDomains.map((r, i) => (
                <tr key={i}>
                  <td className="mono" style={{ color: 'var(--text-primary)' }}>{r.bundleId}</td>
                  <td>
                    {r.platform === 'ios'         && <span className="badge badge-cyan">iOS</span>}
                    {r.platform === 'android'     && <span className="badge badge-green">Android</span>}
                    {r.platform === 'roku'        && <span className="badge badge-purple">Roku</span>}
                    {r.platform === 'tvos'        && <span className="badge badge-cyan">tvOS</span>}
                    {r.platform === 'android_tv'  && <span className="badge badge-green">Android TV</span>}
                    {r.platform === 'firetv'      && <span className="badge badge-amber">Fire TV</span>}
                    {r.platform === 'samsung'     && <span className="badge badge-muted" style={{ color:'#1428A0' }}>Samsung</span>}
                    {r.platform === 'androidtv'   && <span className="badge badge-green">Android TV</span>}
                    {r.platform === 'lg'          && <span className="badge badge-red">LG</span>}
                    {r.platform === 'vizio'       && <span className="badge badge-amber">Vizio</span>}
                    {r.platform === 'unknown_ctv' && <span className="badge badge-muted">CTV</span>}
                    {r.platform === 'unknown'     && <span className="badge badge-muted">Unknown</span>}
                  </td>
                  <td>
                    {r.domain
                      ? <code style={{ fontSize: '12px', color: 'var(--cyan)' }}>{r.domain}</code>
                      : <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>}
                  </td>
                  <td>
                    {r.domain
                      ? <span className="badge badge-green"><CheckCircle size={10} /> Resolved</span>
                      : <span className="badge badge-red"><XCircle size={10} /> {r.error || 'Not found'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Collapsible bundle list panel ────────────────────────────────────────────
// Shared by the "All Lines Present" and "Custom Line Selection" result cards.
// Collapsed by default: shows count + Expand toggle + Copy + Export CSV. When
// expanded, renders a vertical list (platform icon · app name · bundle ID · domain).
function BundleListPanel({ identifiers, inputMode, expanded, onToggle, onCopy, onExport }) {
  const isBundles = inputMode === 'bundles' || inputMode === 'ctv';
  const count = identifiers?.length || 0;

  function PlatformIcon({ platform }) {
    if (platform === 'android' || platform === 'android_tv') return <Smartphone size={12} style={{ color: 'var(--green)', flexShrink: 0 }} />;
    if (platform === 'ios' || platform === 'tvos') return <Layers size={12} style={{ color: 'var(--cyan)', flexShrink: 0 }} />;
    if (platform === 'roku') return <span style={{ fontSize: '11px', flexShrink: 0 }}>📺</span>;
    if (platform === 'firetv') return <span style={{ fontSize: '11px', flexShrink: 0 }}>🔥</span>;
    return <Globe size={12} style={{ color: 'var(--green)', flexShrink: 0 }} />;
  }

  return (
    <div>
      {/* Control bar: count + expand toggle + copy + export */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button
          onClick={onToggle}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', padding: '4px 0', fontSize: '12px', fontWeight: 600 }}>
          {expanded ? <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />}
          {count} bundle{count === 1 ? '' : 's'}
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400 }}>
            {expanded ? '· click to collapse' : '· click to expand'}
          </span>
        </button>
        <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
          <button className="btn btn-secondary btn-sm" onClick={onCopy} disabled={!count}>
            <Copy size={12} /> Copy IDs
          </button>
          <button className="btn btn-secondary btn-sm" onClick={onExport} disabled={!count}>
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Expanded vertical list */}
      {expanded && count > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '10px' }}>
          {identifiers.map((item, i) => {
            const label = isBundles ? (item.appName || item.bundleId || item.domain || '—') : (item.domain || '—');
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                background: 'color-mix(in srgb, var(--green) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--green) 18%, transparent)',
                fontSize: '12px',
              }}>
                <PlatformIcon platform={item.platform} />
                <span style={{ color: 'var(--green)', fontWeight: 600, minWidth: '180px', flexShrink: 0 }}>{label}</span>
                {isBundles && item.bundleId && (
                  <code style={{ color: 'var(--text-muted)', fontSize: '11px', fontFamily: 'var(--font-mono)', minWidth: '200px', flexShrink: 0 }}>{item.bundleId}</code>
                )}
                {item.domain && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '11px', flex: 1, wordBreak: 'break-all' }}>{item.domain}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Crawler page ────────────────────────────────────────────────────────
export default function Crawler() {
  const [inputMode, setInputMode] = useState('domains'); // 'domains' | 'bundles' | 'ctv'
  const [domains, setDomains] = useState('');
  const [bundleIds, setBundleIds] = useState('');
  const [ctvBundleIds, setCtvBundleIds] = useState('');
  const [searchLines, setSearchLines] = useState('');
  const [type, setType] = useState('ads.txt');
  const [concurrency, setConcurrency] = useState(5);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [resultFilter, setResultFilter] = useState('all');
  const [crawlPage, setCrawlPage] = useState(1);
  const [crawlPageSize, setCrawlPageSize] = useState(500);
  const [selectedLineIndices, setSelectedLineIndices] = useState(new Set()); // for custom line selection
  // Collapse state for the two result-card bundle lists. Collapsed by default —
  // these lists can be long, so we show a count + Expand toggle instead.
  const [allLinesExpanded,    setAllLinesExpanded]    = useState(false);
  const [customLinesExpanded, setCustomLinesExpanded] = useState(false);

  const pollRef = useRef(null);

  useEffect(() => {
    loadJobs();
    return () => clearInterval(pollRef.current);
  }, []);

  async function loadJobs() {
    try { const { data } = await axios.get('/api/crawler/jobs'); setJobs(data); } catch {}
  }

  async function stopCrawl() {
    if (!jobId) return;
    try {
      clearInterval(pollRef.current);
      await axios.post(`/api/crawler/job/${jobId}/cancel`);
      setJob(prev => prev ? { ...prev, status: 'cancelled' } : prev);
      setLoading(false);
      toast('Crawl stopped.', { icon: '⛔' });
    } catch { toast.error('Failed to stop crawl'); }
  }

  async function startCrawl() {
    const lineList = searchLines.split('\n').map(l => l.trim()).filter(Boolean);
    if (lineList.length > 10) { toast.error('Maximum 10 search lines per job. Please reduce your Lines to Search.'); setLoading(false); return; }
    setLoading(true); setJob(null); clearInterval(pollRef.current);

    try {
      let payload = { type: (inputMode === 'ctv' || inputMode === 'bundles') ? 'app-ads.txt' : type, concurrency: parseInt(concurrency), searchLines: lineList, inputMode };

      if (inputMode === 'bundles') {
        const bundleList = bundleIds.split('\n').map(d => d.trim()).filter(Boolean);
        if (bundleList.length === 0) { toast.error('Enter at least one bundle ID'); setLoading(false); return; }
        if (bundleList.length > 1000) { toast.error('Maximum 1000 bundle IDs per job'); setLoading(false); return; }
        payload.bundleIds = bundleList;
        toast.success(`Starting — resolving ${bundleList.length} mobile bundle ID${bundleList.length > 1 ? 's' : ''} from app stores...`);
      } else if (inputMode === 'ctv') {
        const ctvList = ctvBundleIds.split('\n').map(d => d.trim()).filter(Boolean);
        if (ctvList.length === 0) { toast.error('Enter at least one CTV bundle ID'); setLoading(false); return; }
        if (ctvList.length > 500) { toast.error('Maximum 500 CTV bundle IDs per job'); setLoading(false); return; }
        payload.ctvBundleIds = ctvList;
        toast.success(`Starting — resolving ${ctvList.length} CTV bundle ID${ctvList.length > 1 ? 's' : ''} from CTV stores...`);
      } else {
        const domainList = domains.split('\n').map(d => d.trim()).filter(Boolean);
        if (domainList.length === 0) { toast.error('Enter at least one domain'); setLoading(false); return; }

        if (domainList.length > 500) { toast.error('Maximum 500 domains per job'); setLoading(false); return; }
        payload.domains = domainList;
        toast.success(`Crawl started — ${domainList.length} domain${domainList.length > 1 ? 's' : ''}${lineList.length ? `, ${lineList.length} line${lineList.length > 1 ? 's' : ''} to search` : ''}`);
      }

      const { data } = await axios.post('/api/crawler/start', payload);
      setJobId(data.jobId);
      setJob({ ...data, results: [], searchLines: lineList, resolvedDomains: [] });
      pollRef.current = setInterval(() => pollJob(data.jobId), 2000);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }

  async function pollJob(id) {
    try {
      const { data } = await axios.get(`/api/crawler/job/${id}`);
      setJob(data);
      if (data.status === 'completed' || data.status === 'cancelled') {
        clearInterval(pollRef.current); loadJobs();
        if (data.status === 'completed') {
          toast.success(`Done — ${data.successful}/${data.total} succeeded`);
          // Initialize all line indices as selected for the custom selection tool
          if (data.searchLines?.length > 0)
            setSelectedLineIndices(new Set(data.searchLines.map((_, i) => i)));
        }
      }
    } catch {}
  }

  async function loadJob(id) {
    clearInterval(pollRef.current);
    try {
      const { data } = await axios.get(`/api/crawler/job/${id}`);
      setJob(data); setJobId(id);
      if (data.searchLines?.length > 0)
        setSelectedLineIndices(new Set(data.searchLines.map((_, i) => i)));
      if (data.status === 'running' || data.status === 'resolving')
        pollRef.current = setInterval(() => pollJob(id), 2000);
    } catch {}
  }

  const progress = job ? Math.round(((job.completed || 0) / (job.total || 1)) * 100) : 0;
  const hasSearchLines = job?.searchLines && job.searchLines.length > 0;

  // ── Custom line selection result — computed from existing job.results ────────
  const customLinesResult = useMemo(() => {
    if (!job?.results || !hasSearchLines || selectedLineIndices.size === 0) return null;
    const idxArr = [...selectedLineIndices].sort((a, b) => a - b);
    const successful = job.results.filter(r => r.success && r.lineResults?.length > 0);
    const matching = successful.filter(r => idxArr.every(idx => r.lineResults[idx]?.found));
    return {
      count: matching.length,
      total: successful.length,
      selectedCount: idxArr.length,
      identifiers: matching.map(r => ({
        domain:    r.domain   || null,
        bundleId:  r.bundleId || null,
        appName:   r.appName  || null,
        platform:  r.platform || null,
      })),
    };
  }, [job, selectedLineIndices, hasSearchLines]);
  const filteredResults = (job?.results || []).filter(r => {
    if (resultFilter === 'failed') return !r.success;
    if (resultFilter === 'found') return r.success && r.lineResults && r.lineResults.some(lr => lr.found);
    if (resultFilter === 'notfound') return r.success && r.lineResults && r.lineResults.every(lr => !lr.found);
    return true;
  });

  // Reset to first page whenever the filter or the job changes
  useEffect(() => { setCrawlPage(1); }, [resultFilter, jobId]);

  // Rows for a given filter id (independent of the active filter) — for per-filter copy
  function categoryRows(id) {
    return (job?.results || []).filter(r => {
      if (id === 'failed')   return !r.success;
      if (id === 'found')    return r.success && r.lineResults && r.lineResults.some(lr => lr.found);
      if (id === 'notfound') return r.success && r.lineResults && r.lineResults.every(lr => !lr.found);
      return true;
    });
  }

  const crawlTotalPages = Math.max(1, Math.ceil(filteredResults.length / crawlPageSize));
  const crawlSafePage = Math.min(crawlPage, crawlTotalPages);
  const pagedResults = filteredResults.slice((crawlSafePage - 1) * crawlPageSize, (crawlSafePage - 1) * crawlPageSize + crawlPageSize);

  const activeInputCount = inputMode === 'bundles'
    ? bundleIds.split('\n').filter(l => l.trim()).length
    : inputMode === 'ctv'
    ? ctvBundleIds.split('\n').filter(l => l.trim()).length
    : domains.split('\n').filter(l => l.trim()).length;

  function exportCSV() {
    if (!job?.results?.length) return;
    const hasLines = job.searchLines?.length > 0;
    const rows = [];

    // CTV results carry bundleId/appName/platform exactly as mobile ones do —
    // the backend fan-out handles both modes together — so the export must treat
    // them together too. Branching on 'bundles' alone dropped every CTV input id.
    const hasBundleIds = job.inputMode === 'bundles' || job.inputMode === 'ctv';

    // Header
    if (hasBundleIds) {
      if (hasLines) {
        rows.push(['Bundle ID', 'App Name', 'Platform', 'Developer Domain', ...job.searchLines.flatMap(l => [`"${l}" Found`, `"${l}" Occurrences`, `"${l}" as in file`])]);
      } else {
        rows.push(['Bundle ID', 'App Name', 'Platform', 'Developer Domain', 'Total Records', 'DIRECT', 'RESELLER', 'Status']);
      }
    } else {
      if (hasLines) {
        rows.push(['Domain', ...job.searchLines.flatMap(l => [`"${l}" Found`, `"${l}" Occurrences`, `"${l}" as in file`])]);
      } else {
        rows.push(['Domain', 'Total Records', 'DIRECT', 'RESELLER', 'Status']);
      }
    }

    // Data rows
    job.results.forEach(r => {
      const base = hasBundleIds
        ? [r.bundleId || '', r.appName || '', r.platform || '', r.domain || '']
        : [r.domain || ''];

      if (hasLines) {
        const lineData = (job.searchLines || []).flatMap((line, i) => {
          const lr = r.lineResults?.[i];
          return [
            lr?.found ? 'Found' : 'Not Found',
            lr?.duplicateCount ?? 0,
            // ALL matched lines, not just the first. A domain search returns every
            // line an exchange has in the file, and keeping only [0] silently
            // implied there was one. Newline-separated inside the quoted cell,
            // which Excel and Sheets both render as a multi-line cell.
            (lr?.matchedLines || []).join('\n')
          ];
        });
        rows.push([...base, ...lineData]);
      } else {
        const stats = [r.totalRecords ?? '', r.directCount ?? '', r.resellerCount ?? '', r.success ? 'Success' : (r.error || 'Failed')];
        rows.push([...base, ...stats]);
      }
    });

    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `crawler-results-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success('Exported!');
  }

  // ─── Helpers for the two result-card bundle lists (All Lines / Custom) ───────
  // Copy just the bundle IDs (newline-separated) for quick paste elsewhere.
  function copyBundleIds(identifiers) {
    const ids = (identifiers || [])
      .map(it => it.bundleId || it.domain || '')
      .filter(Boolean);
    if (!ids.length) { toast.error('Nothing to copy'); return; }
    navigator.clipboard.writeText(ids.join('\n'));
    toast.success(`Copied ${ids.length} bundle ID${ids.length === 1 ? '' : 's'}`);
  }

  // Export a result-card's matching identifiers to CSV. `criteriaLines` is the
  // list of search lines that defined this result (all lines for "All Lines
  // Present"; the selected subset for "Custom Line Selection"). Those are written
  // as comment header rows above the data table so the export is self-documenting.
  function exportBundleListCsv(identifiers, criteriaLines, filenamePrefix) {
    const list = identifiers || [];
    if (!list.length) { toast.error('Nothing to export'); return; }

    const commentRows = [
      ['# Crawler — bundles matching all criteria lines below'],
      ...(criteriaLines || []).map((line, i) => [`# Line ${i + 1}: ${line}`]),
      ['# Matches: ' + list.length],
      [''],   // blank separator row
    ];
    const headerRow = ['Bundle ID', 'App Name', 'Platform', 'Developer Domain'];
    const dataRows = list.map(it => [
      it.bundleId || '',
      it.appName || '',
      it.platform || '',
      it.domain || '',
    ]);

    const allRows = [...commentRows, headerRow, ...dataRows];
    const csv = allRows
      .map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success(`Exported ${list.length} bundles`);
  }

  return (
    <div>
      <h1 className="page-title">Ads.txt / App-ads.txt Crawler</h1>
      <p className="page-subtitle">Bulk crawl domains or bundle IDs and search for specific ads.txt lines, with automatic sellers.json cross-check</p>

      {/* Input mode toggle */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '2px', marginBottom: '14px', borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
          <button
            className={`btn btn-sm ${inputMode === 'domains' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setInputMode('domains')}
          >
            <Globe size={13} /> Domain List
          </button>
          <button
            className={`btn btn-sm ${inputMode === 'bundles' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setInputMode('bundles')}
          >
            <Smartphone size={13} /> Mobile App Bundles
          </button>
          <button
            className={`btn btn-sm ${inputMode === 'ctv' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setInputMode('ctv')}
          >
            <Layers size={13} /> CTV Bundles
            <span style={{ fontSize:'8px', fontWeight:800, letterSpacing:'0.6px', background:'var(--amber)', color:'var(--bg-base)', borderRadius:'3px', padding:'1px 5px', marginLeft:'6px' }}>BETA</span>
          </button>
        </div>

        {/* Two-column input */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

          {/* Left panel — domains, mobile bundles, or CTV bundles */}
          <div>
            <div className="card-title" style={{ marginBottom: '8px' }}>
              {inputMode === 'domains'
                ? <><Globe size={13} /> Domain List</>
                : inputMode === 'ctv'
                ? <><Layers size={13} /> CTV Bundle IDs</>
                : <><Smartphone size={13} /> Mobile App Bundle IDs</>}
              <span className="badge badge-muted" style={{ marginLeft: 'auto' }}>
                {activeInputCount} {inputMode === 'bundles' ? 'bundles' : inputMode === 'ctv' ? 'CTV bundles' : 'domains'}
              </span>
            </div>

            {inputMode === 'domains' && (
              <textarea
                className="input-field w-full"
                style={{ minHeight: '200px', fontSize: '12px' }}
                placeholder={'publisher1.com\npublisher2.com\nlearning.ai\n...'}
                value={domains}
                onChange={e => setDomains(e.target.value)}
              />
            )}

            {inputMode === 'bundles' && (
              <>
                <textarea
                  className="input-field w-full"
                  style={{ minHeight: '200px', fontSize: '12px' }}
                  placeholder={'iOS example:\n1473573813\n\nAndroid example:\ncom.supercell.clashofclans\ncom.king.candycrushsaga'}
                  value={bundleIds}
                  onChange={e => setBundleIds(e.target.value)}
                />
                <div style={{ marginTop: '6px', fontSize: '10px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  iOS: numeric App Store ID · Android: reverse-domain package name
                </div>
              </>
            )}

            {inputMode === 'ctv' && (
              <>
                <textarea
                  className="input-field w-full"
                  style={{ minHeight: '200px', fontSize: '12px' }}
                  placeholder={'Roku:\n13535\n19977\n\nApple TV (tvOS):\n1473573813\n\nAndroid TV:\ncom.netflix.ninja\n\nFire TV (ASIN):\nB00QXKFNK2\n\nSamsung:\nG15147002586'}
                  value={ctvBundleIds}
                  onChange={e => setCtvBundleIds(e.target.value)}
                />
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '10px', lineHeight: 1.7 }}>
                  <div><span style={{ color: 'var(--cyan)', fontWeight: 700 }}>Roku</span><span style={{ color: 'var(--text-muted)' }}> — short numeric · e.g. 13535</span></div>
                  <div><span style={{ color: 'var(--green)', fontWeight: 700 }}>Android TV</span><span style={{ color: 'var(--text-muted)' }}> — reverse-domain · e.g. com.netflix.ninja</span></div>
                  <div><span style={{ color: 'var(--amber)', fontWeight: 700 }}>Apple TV (tvOS)</span><span style={{ color: 'var(--text-muted)' }}> — 9-10 digit App Store ID · e.g. 1473573813</span></div>
                  <div><span style={{ color: 'var(--purple)', fontWeight: 700 }}>Fire TV (Amazon)</span><span style={{ color: 'var(--text-muted)' }}> — ASIN · e.g. B00QXKFNK2</span></div>
                  <div><span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>Samsung</span><span style={{ color: 'var(--text-muted)' }}> — numeric or G-prefix app ID · resolved via verified mappings</span></div>
                  <div><span style={{ color: 'var(--red)', fontWeight: 700 }}>LG</span><span style={{ color: 'var(--text-muted)' }}> — numeric app ID · resolved via verified mappings</span></div>
                  <div><span style={{ color: 'var(--amber)', fontWeight: 700 }}>Vizio</span><span style={{ color: 'var(--text-muted)' }}> — reverse-domain ID · resolved via verified mappings</span></div>
                </div>
              </>
            )}
          </div>

          {/* Right panel — lines to search */}
          <div>
            <div className="card-title" style={{ marginBottom: '8px' }}>
              <Search size={13} /> Lines to Search
              <span className="badge badge-cyan" style={{ marginLeft: 'auto', fontSize: '10px' }}>optional</span>
            </div>
            <textarea
              className="input-field w-full"
              style={{ minHeight: '200px', fontSize: '12px' }}
              placeholder={'Paste full ads.txt lines to search for:\n\nthemediagrid.com, 4WPRHV, RESELLER, 9fac4a4a87c2a44f\nrubiconproject.com, 23282, RESELLER, 0bfd66d529a55807\n\nOr just an exchange domain to find every line it has:\nfirstimpression.io\n\nSpacing does not matter.'}
              value={searchLines}
              onChange={e => setSearchLines(e.target.value)}
            />
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '6px' }}>
              ✦ Leave empty to crawl without line search
            </div>
          </div>
        </div>



        {/* Controls row */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
          {(inputMode === 'ctv' || inputMode === 'bundles') ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: 'color-mix(in srgb, var(--cyan) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--cyan) 20%, transparent)', borderRadius: 'var(--radius-sm)', fontSize: '11px', color: 'var(--cyan)', fontWeight: 600 }}>
              app-ads.txt
              <span style={{ fontSize: '9px', color: 'var(--text-muted)', fontWeight: 400 }}>
                {inputMode === 'ctv' ? 'CTV always uses app-ads.txt' : 'Mobile bundles always use app-ads.txt'}
              </span>
            </div>
          ) : (
            <select className="select-field" value={type} onChange={e => setType(e.target.value)}>
              <option value="ads.txt">ads.txt</option>
              <option value="app-ads.txt">app-ads.txt</option>
            </select>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select className="select-field" value={concurrency} onChange={e => setConcurrency(e.target.value)}>
              {[2, 5, 10, 15, 20].map(n => <option key={n} value={n}>Concurrency: {n}</option>)}
            </select>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {concurrency <= 2 && 'Slowest — use for flaky connections'}
              {concurrency == 5 && '⭐ Recommended — balanced speed & stability'}
              {concurrency >= 10 && concurrency < 20 && 'Fast — may hit rate limits'}
              {concurrency >= 20 && 'Fastest — risk of being blocked'}
            </span>
          </div>
          {inputMode === 'bundles' && (
            <span style={{ fontSize: '11px', color: 'var(--amber)' }}>
              <AlertTriangle size={11} style={{ verticalAlign: 'middle' }} /> Bundle mode may take longer to process
            </span>
          )}
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            {(job && (job.status === 'running' || job.status === 'resolving')) && (
              <button
                className="btn btn-danger"
                onClick={stopCrawl}
                style={{ minWidth: '120px' }}
              >
                ⛔ Stop Crawl
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={startCrawl}
              disabled={loading || (job && (job.status === 'running' || job.status === 'resolving'))}
              style={{ minWidth: '130px' }}
            >
              {loading || (job && (job.status === 'running' || job.status === 'resolving'))
                ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> {job?.status === 'resolving' ? 'Resolving...' : 'Running...'}</>
                : <><Play size={14} /> Start Crawl</>}
            </button>
          </div>
          {job && job.status === 'completed' && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button className="btn btn-secondary btn-sm" onClick={exportCSV}>
                <Download size={13} /> Export CSV
              </button>
              <button className="btn btn-secondary" onClick={() => { setJob(null); setJobId(null); }}>Clear Results</button>
            </div>
          )}
        </div>
      </div>

      {/* Recent jobs (when no active job) */}
      {!job && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: '12px' }}><BarChart2 size={14} /> Recent Jobs</div>
          {jobs.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px' }}>
              <div className="empty-state-text">No jobs yet — start a crawl above</div>
            </div>
          ) : jobs.slice(0, 10).map(j => (
            <div key={j.id} onClick={() => loadJob(j.id)} style={{
              padding: '8px 12px', marginBottom: '4px', borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              cursor: 'pointer', fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <span style={{ color: 'var(--text-primary)', marginRight: '8px' }}>{j.type}</span>
                <span style={{ color: 'var(--text-muted)' }}>{j.total} {j.inputMode === 'bundles' ? 'bundles' : 'domains'}</span>
                {j.searchLines && j.searchLines.length > 0 && (
                  <span style={{ color: 'var(--cyan)', marginLeft: '6px' }}>· {j.searchLines.length} lines searched</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {j.inputMode === 'bundles' && <span className="badge badge-cyan">Bundles</span>}
                <span className="badge badge-green">{j.successful} ok</span>
                {j.failed > 0 && <span className="badge badge-red">{j.failed} failed</span>}
                <span className={`badge ${j.status === 'completed' ? 'badge-green' : 'badge-amber'}`}>{j.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Active job */}
      {job && (
        <>
          {/* Progress card */}
          <div className="card" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div className="card-title">
                {job.status === 'resolving'
                  ? <><Layers size={14} style={{ animation: 'spin 1s linear infinite' }} /> Resolving bundle IDs from app stores...</>
                  : job.status === 'running'
                  ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Crawling...</>
                  : <><CheckCircle size={14} style={{ color: 'var(--green)' }} /> Complete</>}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {job.inputMode === 'bundles' && <span className="badge badge-cyan">Bundle Mode</span>}
                <span className={`badge ${job.status === 'completed' ? 'badge-green' : 'badge-amber'}`}>{job.status}</span>
                <span className="badge badge-muted">{progress}%</span>
              </div>
            </div>

            <div className="progress-bar" style={{ marginBottom: '14px' }}>
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>

            <div className="grid-4">
              <div className="stat-card cyan"><div className="stat-label">Total</div><div className="stat-value">{job.total}</div></div>
              <div className="stat-card green"><div className="stat-label">Success</div><div className="stat-value green">{job.successful}</div></div>
              <div className="stat-card red"><div className="stat-label">Failed</div><div className="stat-value red">{job.failed}</div></div>
              <div className="stat-card amber">
                <div className="stat-label">{job.status === 'resolving' ? 'Resolving' : 'Remaining'}</div>
                <div className="stat-value amber">{job.total - (job.completed || 0)}</div>
              </div>
            </div>

            {/* Bundle resolution failed warning */}
            {job.resolutionFailed > 0 && (
              <div className="alert alert-warning" style={{ marginTop: '12px' }}>
                <AlertTriangle size={12} />
                {job.resolutionFailed} bundle ID{job.resolutionFailed > 1 ? 's' : ''} could not be resolved — they were skipped.
              </div>
            )}
          </div>

          {/* Bundle belongs to Developer */}
          {(job.inputMode === 'bundles' || job.inputMode === 'ctv') && job.resolvedDomains?.length > 0 && (
            <ResolutionSummary resolvedDomains={job.resolvedDomains} inputMode={job.inputMode} />
          )}

          {/* Search lines summary */}
          {job.status === 'completed' && hasSearchLines && job.summary?.lineStats && (
            <div className="card" style={{ marginBottom: '16px' }}>
              <div className="card-title" style={{ marginBottom: '12px' }}><Search size={14} /> Search Lines — Summary</div>
              <table className="data-table">
                <thead>
                  <tr><th>#</th><th>Line Searched</th><th>Domains Found In</th><th>Total Occurrences</th><th>Checked</th><th></th></tr>
                </thead>
                <tbody>
                  {job.summary.lineStats.map((ls, i) => (
                    <tr key={i}>
                      <td className="text-muted">{i + 1}</td>
                      <td><code style={{ fontSize: '11px', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{ls.line}</code></td>
                      <td><span className={`badge ${ls.domainsFound > 0 ? 'badge-green' : 'badge-red'}`}>{ls.domainsFound} / {ls.domainsChecked}</span></td>
                      <td>{ls.totalDuplicates > 0 ? <span className="badge badge-cyan">{ls.totalDuplicates}</span> : <span className="badge badge-muted">0</span>}</td>
                      <td className="text-muted">{ls.domainsChecked}</td>
                      <td>
                        <button className="btn btn-secondary btn-sm" title="Copy IDs / domains where this line was found" disabled={!ls.domainsFound} style={{ padding: '4px 7px' }}
                          onClick={() => copyBundleIds((job.results || []).filter(r => r.success && r.lineResults?.[i]?.found))}>
                          <Copy size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* All Lines Present — only shown when 2+ search lines and job complete */}
          {job.status === 'completed' && hasSearchLines && job.summary?.allLinesFound && (() => {
            const alf = job.summary.allLinesFound;
            const isBundles = job.inputMode === 'bundles';
            const isCTV     = job.inputMode === 'ctv';
            const lineCount = job.searchLines?.length || 0;
            const modeLabel = isBundles ? 'Mobile bundles' : isCTV ? 'CTV bundles' : 'Domains';
            return (
              <div className="card" style={{ marginBottom: '16px', border: `1px solid ${alf.count > 0 ? 'color-mix(in srgb, var(--green) 30%, transparent)' : 'color-mix(in srgb, var(--red) 20%, transparent)'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: alf.count > 0 ? '14px' : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {alf.count > 0
                      ? <CheckCircle size={16} style={{ color: 'var(--green)', flexShrink: 0 }} />
                      : <XCircle    size={16} style={{ color: 'var(--red)',   flexShrink: 0 }} />}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>
                        All {lineCount} Lines Present
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {modeLabel} whose app-ads.txt contains every searched line
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '22px', fontWeight: 800, color: alf.count > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                      {alf.count}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>of {alf.total} successful</div>
                  </div>
                </div>

                {alf.count > 0 && (
                  <BundleListPanel
                    identifiers={alf.identifiers}
                    inputMode={job.inputMode}
                    expanded={allLinesExpanded}
                    onToggle={() => setAllLinesExpanded(v => !v)}
                    onCopy={() => copyBundleIds(alf.identifiers)}
                    onExport={() => exportBundleListCsv(alf.identifiers, job.searchLines, 'crawler-all-lines')}
                  />
                )}

                {alf.count === 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                    No {modeLabel.toLowerCase()} had all {lineCount} lines present in its app-ads.txt simultaneously.
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Custom Line Selection ─────────────────────────────────────────────
               Lets users pick any subset of the searched lines and instantly see
               how many bundles/domains have all of those selected lines present. */}
          {job.status === 'completed' && hasSearchLines && job.searchLines.length >= 2 && (
            <div className="card" style={{ marginBottom: '16px', border: '1px solid color-mix(in srgb, var(--cyan) 20%, transparent)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div className="card-title" style={{ margin: 0 }}>
                  <Filter size={14} /> Custom Line Selection
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '8px' }}>
                    Pick any combination of lines to see how many results contain all of them simultaneously
                  </span>
                </div>
              </div>

              {/* Line checkboxes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
                {job.searchLines.map((line, i) => {
                  const stat = job.summary?.lineStats?.[i];
                  const isZero = stat && stat.domainsFound === 0;
                  const isChecked = selectedLineIndices.has(i);
                  return (
                    <label key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '7px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      background: isChecked ? 'color-mix(in srgb, var(--cyan) 6%, transparent)' : 'var(--bg-elevated)',
                      border: `1px solid ${isChecked ? 'color-mix(in srgb, var(--cyan) 25%, transparent)' : 'var(--border)'}`,
                      transition: 'all 0.12s',
                    }}
                      onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = 'var(--bg-card)'; }}
                      onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = 'var(--bg-elevated)'; }}>
                      <input type="checkbox" checked={isChecked}
                        style={{ accentColor: 'var(--cyan)', cursor: 'pointer', flexShrink: 0 }}
                        onChange={() => setSelectedLineIndices(prev => {
                          const next = new Set(prev);
                          next.has(i) ? next.delete(i) : next.add(i);
                          return next;
                        })} />
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700, minWidth: '20px' }}>L{i + 1}</span>
                      <code style={{ fontSize: '11px', color: isChecked ? 'var(--text-primary)' : 'var(--text-muted)', flex: 1, wordBreak: 'break-all' }}>{line}</code>
                      {stat && (
                        <span className={`badge ${isZero ? 'badge-red' : 'badge-green'}`} style={{ fontSize: '9px', flexShrink: 0 }}>
                          {stat.domainsFound} / {stat.domainsChecked}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              {/* Quick-select buttons */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedLineIndices(new Set(job.searchLines.map((_, i) => i)))}>
                  Select All
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedLineIndices(new Set())}>
                  Clear
                </button>
                {job.summary?.lineStats?.some(ls => ls.domainsFound === 0) && (
                  <button className="btn btn-secondary btn-sm" style={{ color: 'var(--cyan)' }}
                    onClick={() => setSelectedLineIndices(new Set(
                      job.summary.lineStats.map((ls, i) => ls.domainsFound > 0 ? i : null).filter(i => i !== null)
                    ))}>
                    ✦ Deselect 0-find lines
                  </button>
                )}
                {selectedLineIndices.size > 0 && (
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', alignSelf: 'center', marginLeft: 'auto' }}>
                    {selectedLineIndices.size} of {job.searchLines.length} lines selected
                  </span>
                )}
              </div>

              {/* Live result */}
              {customLinesResult && selectedLineIndices.size >= 2 ? (
                <div style={{
                  padding: '12px 14px', borderRadius: 'var(--radius-sm)',
                  background: customLinesResult.count > 0 ? 'color-mix(in srgb, var(--green) 5%, transparent)' : 'color-mix(in srgb, var(--red) 5%, transparent)',
                  border: `1px solid ${customLinesResult.count > 0 ? 'color-mix(in srgb, var(--green) 25%, transparent)' : 'color-mix(in srgb, var(--red) 20%, transparent)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: customLinesResult.count > 0 ? '12px' : '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {customLinesResult.count > 0
                        ? <CheckCircle size={14} style={{ color: 'var(--green)', flexShrink: 0 }} />
                        : <XCircle    size={14} style={{ color: 'var(--red)',   flexShrink: 0 }} />}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>
                          All {customLinesResult.selectedCount} Selected Lines Present
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
                          {job.inputMode === 'bundles' ? 'Mobile bundles' : job.inputMode === 'ctv' ? 'CTV bundles' : 'Domains'} that contain every selected line simultaneously
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '20px', fontWeight: 800, color: customLinesResult.count > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                        {customLinesResult.count}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>of {customLinesResult.total} successful</div>
                    </div>
                  </div>

                  {customLinesResult.count > 0 && (
                    <BundleListPanel
                      identifiers={customLinesResult.identifiers}
                      inputMode={job.inputMode}
                      expanded={customLinesExpanded}
                      onToggle={() => setCustomLinesExpanded(v => !v)}
                      onCopy={() => copyBundleIds(customLinesResult.identifiers)}
                      onExport={() => exportBundleListCsv(
                        customLinesResult.identifiers,
                        [...selectedLineIndices].sort((a, b) => a - b).map(i => job.searchLines[i]),
                        'crawler-custom-lines'
                      )}
                    />
                  )}

                  {customLinesResult.count === 0 && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      No results had all {customLinesResult.selectedCount} selected lines present simultaneously.
                    </div>
                  )}
                </div>
              ) : selectedLineIndices.size === 1 ? (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '8px 0' }}>
                  Select at least 2 lines to compute a combined result.
                </div>
              ) : selectedLineIndices.size === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '8px 0' }}>
                  No lines selected — check at least 2 lines above to see results.
                </div>
              ) : null}
            </div>
          )}
          {job.results && job.results.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginRight: '4px' }}>Filter:</span>
              {[
                { id: 'all', label: `All (${job.results.length})` },
                ...(hasSearchLines ? [{ id: 'found', label: 'Lines Found' }, { id: 'notfound', label: 'Not Found' }] : []),
                { id: 'failed', label: 'Failed' }
              ].map(f => (
                <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                  <button className={`btn btn-sm ${resultFilter === f.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setResultFilter(f.id)}>
                    {f.label}
                  </button>
                  {f.id !== 'all' && (
                    <button className="btn btn-secondary btn-sm" title={`Copy ${f.label} IDs / domains`} style={{ padding: '4px 7px' }} onClick={() => copyBundleIds(categoryRows(f.id))}>
                      <Copy size={11} />
                    </button>
                  )}
                </span>
              ))}
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                Showing {filteredResults.length} of {job.results.length}
              </span>
            </div>
          )}

          {/* Results */}
          {filteredResults.length > 0 && (
            <>
              {(job.inputMode === 'bundles' || job.inputMode === 'ctv')
                ? <BundleResultsTable
                    results={pagedResults}
                    searchLines={job.searchLines || []}
                    inputMode={job.inputMode}
                  />
                : pagedResults.map((r, i) => (
                    <DomainResultCard key={(r.domain || r.bundleId) + i} result={r} inputMode={job.inputMode} />
                  ))}
              {filteredResults.length > 250 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Showing {((crawlSafePage - 1) * crawlPageSize + 1).toLocaleString()}-{Math.min(crawlSafePage * crawlPageSize, filteredResults.length).toLocaleString()} of {filteredResults.length.toLocaleString()}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <select className="select-field" value={crawlPageSize} onChange={e => { setCrawlPageSize(parseInt(e.target.value)); setCrawlPage(1); }} style={{ fontSize: '12px' }}>
                      <option value={250}>250 / page</option>
                      <option value={500}>500 / page</option>
                      <option value={1000}>1000 / page</option>
                    </select>
                    <button className="btn btn-secondary btn-sm" disabled={crawlSafePage <= 1} onClick={() => setCrawlPage(pn => Math.max(1, pn - 1))}><ChevronLeft size={12} /> Prev</button>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '74px', textAlign: 'center' }}>Page {crawlSafePage} / {crawlTotalPages}</span>
                    <button className="btn btn-secondary btn-sm" disabled={crawlSafePage >= crawlTotalPages} onClick={() => setCrawlPage(pn => Math.min(crawlTotalPages, pn + 1))}>Next <ChevronRight size={12} /></button>
                  </div>
                </div>
              )}
            </>
          )}

          {(job.status === 'running' || job.status === 'resolving') && job.results.length === 0 && (
            <div className="empty-state">
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              <div className="empty-state-text">
                {job.status === 'resolving'
                  ? job.inputMode === 'ctv'
                    ? 'Resolving CTV bundles to developer domains...'
                    : 'Fetching developer domains from app stores...'
                  : 'Crawling domains...'}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
